/**
 * Cross-tab recording lock using BroadcastChannel.
 *
 * Prevents concurrent audio recordings across browser tabs.
 * Modeled after client/src/utils/tabCounter.ts — uses heartbeat
 * + stale threshold for crash recovery.
 */

const CHANNEL_NAME = 'videa_recording_lock';
const HEARTBEAT_INTERVAL_MS = 5_000;
const STALE_THRESHOLD_MS = 12_000;
const STALE_CHECK_INTERVAL_MS = 5_000;

const TAB_ID =
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

interface RecordingLockMessage {
  type: 'recording' | 'stopped' | 'close';
  tabId: string;
}

// Module state
let channel: BroadcastChannel | null = null;
let initialized = false;
let localLockHeld = false;
let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
let staleCheckInterval: ReturnType<typeof setInterval> | null = null;
let onLockReleasedCallback: (() => void) | null = null;

// Remote lock state
let remoteRecordingTabId: string | null = null;
let remoteLastHeartbeat = 0;

function broadcast(message: RecordingLockMessage): void {
  try {
    channel?.postMessage(message);
  } catch {
    // Channel may be closed
  }
}

function clearRemoteLock(): void {
  const wasLocked = remoteRecordingTabId !== null;
  remoteRecordingTabId = null;
  remoteLastHeartbeat = 0;
  if (wasLocked) {
    onLockReleasedCallback?.();
  }
}

function handleMessage(event: MessageEvent<RecordingLockMessage>): void {
  const { type, tabId } = event.data;

  if (tabId === TAB_ID) return; // Ignore own messages

  switch (type) {
    case 'recording':
      remoteRecordingTabId = tabId;
      remoteLastHeartbeat = Date.now();
      break;
    case 'stopped':
    case 'close':
      if (remoteRecordingTabId === tabId) {
        clearRemoteLock();
      }
      break;
  }
}

function checkStale(): void {
  if (!remoteRecordingTabId) return; // Short-circuit — nothing to check
  if (Date.now() - remoteLastHeartbeat > STALE_THRESHOLD_MS) {
    clearRemoteLock();
  }
}

function startHeartbeat(): void {
  stopHeartbeat();
  broadcast({ type: 'recording', tabId: TAB_ID });
  heartbeatInterval = setInterval(() => {
    broadcast({ type: 'recording', tabId: TAB_ID });
  }, HEARTBEAT_INTERVAL_MS);
}

function stopHeartbeat(): void {
  if (heartbeatInterval !== null) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

function handleBeforeUnload(): void {
  if (localLockHeld) {
    broadcast({ type: 'close', tabId: TAB_ID });
  }
  stopHeartbeat();
}

/** Initialize the lock system. Call once on mount. */
export function initRecordingLock(onLockReleased?: () => void): void {
  if (initialized) return;

  onLockReleasedCallback = onLockReleased ?? null;

  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = handleMessage;
  } catch {
    // BroadcastChannel unavailable — all functions become no-ops
    initialized = true;
    return;
  }

  initialized = true;
  staleCheckInterval = setInterval(checkStale, STALE_CHECK_INTERVAL_MS);
  window.addEventListener('beforeunload', handleBeforeUnload);
}

/**
 * Attempt to acquire the recording lock for this tab.
 * Returns true if acquired, false if another tab holds it.
 */
export function acquireRecordingLock(): boolean {
  if (!initialized || !channel) return true; // Graceful degradation

  // Prune stale before checking
  checkStale();

  if (remoteRecordingTabId) return false;

  localLockHeld = true;
  startHeartbeat();
  return true;
}

/** Release the lock (call on stop/clear). */
export function releaseRecordingLock(): void {
  if (!localLockHeld) return;

  localLockHeld = false;
  stopHeartbeat();
  broadcast({ type: 'stopped', tabId: TAB_ID });
}

/** Read-only check: is another tab currently recording? */
export function isRecordingLockedByOtherTab(): boolean {
  if (!initialized || !channel) return false; // Graceful degradation

  checkStale();
  return remoteRecordingTabId !== null;
}

/** Tear down the lock system. Call on unmount. Nullifies onLockReleased callback. */
export function destroyRecordingLock(): void {
  if (localLockHeld) {
    broadcast({ type: 'close', tabId: TAB_ID });
  }
  stopHeartbeat();
  if (staleCheckInterval !== null) {
    clearInterval(staleCheckInterval);
    staleCheckInterval = null;
  }
  channel?.close();
  channel = null;
  initialized = false;
  localLockHeld = false;
  remoteRecordingTabId = null;
  remoteLastHeartbeat = 0;
  onLockReleasedCallback = null;
  window.removeEventListener('beforeunload', handleBeforeUnload);
}
